// Banner de sugerencia de typo en el dominio del email (signup). En vez de
// pedirle al usuario que compare dos strings casi iguales ("gamil.com" vs
// "gmail.com" es el juego de las 7 diferencias), el error se corrige ante sus
// ojos: un tablero estilo split-flap muestra el dominio tipeado transformándose
// en el sugerido, en loop typo → corregido → typo. Cada operación del edit
// script de Damerau-Levenshtein (lib/emailTypo.ts) tiene su gesto visual:
//
//   transposición → las dos letras se cruzan (una por arriba, otra por abajo)
//   sustitución   → split-flap tipo tablero de aeropuerto
//   inserción     → las mitades se apartan del medio y la letra flapea desde
//                   blanco; una flecha señala el hueco desde arriba (es el
//                   "elemento rojo" cuando la letra que falta no existe aún)
//   borrado       → flapea a blanco, colapsa, las mitades cierran la fila
//
// Colores: `error` para el estado typo y `accent` para el corregido — ambos
// variables de branding, así funciona en tema claro/oscuro de cualquier
// partner. La animación es informativa, nunca bloquea: los botones operan
// desde el primer frame. Con prefers-reduced-motion se muestra el estado
// final estático (dominio corregido con el diff resaltado).
//
// Los estilos (.tf-*) viven en index.css; acá el engine posiciona los cards
// con left/width inline (FLIP manual, sin dependencias).

import { useEffect, useRef } from 'react';
import { btn } from '@/lib/button-styles';
import { domainEditScript, type DomainSuggestion, type EditOp } from '@/lib/emailTypo';

// Dominio sugerido con las letras que CAMBIAN respecto de lo tipeado, en verde
// (mismo criterio que el tablero). "Más breve y evidente" que describirlo en
// prosa. Reusado por el ítem inline (EmailStatus) y el body del popup del typo.
export function ColoredDomain({ typed, suggested }: { typed: string; suggested: string }): JSX.Element {
  const kept = new Set(
    domainEditScript(typed, suggested)
      .filter((op): op is EditOp & { type: 'keep' } => op.type === 'keep')
      .map((op) => op.bi),
  );
  return (
    <span className="font-semibold">
      {suggested.split('').map((ch, i) => (
        <span key={i} className={kept.has(i) ? undefined : 'text-success'}>
          {ch}
        </span>
      ))}
    </span>
  );
}

// Debe coincidir con --tf-speed de .tf-board en index.css: escala los sleeps
// del engine igual que el CSS escala las duraciones de transición.
const SPEED = 0.7;
const NBSP = ' ';
const CANCELLED = Symbol('tf-cancelled');

interface CancelToken {
  cancelled: boolean;
}

function sleep(ms: number, tok: CancelToken): Promise<void> {
  return new Promise((resolve, reject) => {
    setTimeout(() => (tok.cancelled ? reject(CANCELLED) : resolve()), ms * SPEED);
  });
}

// El tablero se dibuja con la tipografía del partner, no con una monoespaciada
// fija: dentro del popup era lo único que ignoraba el branding, y una fuente
// distinta a la del resto del formulario es justo lo que se lee como "esto no
// es de acá".
//
// Las celdas siguen siendo de ancho fijo (un tablero real de aeropuerto lo es),
// así que en vez de asumir que todas las letras miden igual, medimos la MÁS
// ANCHA de las que se van a mostrar y ese es el ancho de celda. Con eso el
// tablero funciona con cualquier fuente que configure el partner.
function brandFont(): string {
  const declared = getComputedStyle(document.documentElement)
    .getPropertyValue('--font-family-brand')
    .trim();
  return declared || 'system-ui, sans-serif';
}

let measureCtx: CanvasRenderingContext2D | null = null;
function measureCellWidth(fs: number, chars: string): number {
  measureCtx ??= document.createElement('canvas').getContext('2d');
  if (!measureCtx) return fs * 0.62; // aproximación si canvas no está
  measureCtx.font = `600 ${fs}px ${brandFont()}`;
  let widest = 0;
  for (const ch of new Set(chars)) {
    widest = Math.max(widest, measureCtx.measureText(ch).width);
  }
  return widest || fs * 0.62;
}

// Letra intermedia para el "traqueteo" del split-flap (los tableros reales
// nunca saltan directo a la letra final).
function wobble(oldC: string, newC: string): string {
  const candidates = [
    String.fromCharCode(newC.charCodeAt(0) + 1),
    String.fromCharCode(newC.charCodeAt(0) - 1),
    'e',
  ];
  for (const c of candidates) {
    if (/[a-z]/.test(c) && c !== oldC && c !== newC) return c;
  }
  return 'x';
}

// Flip secuencial de un card por los chars de `seq` (' ' = card en blanco).
async function flapTo(cell: HTMLSpanElement, seq: string[], tok: CancelToken): Promise<void> {
  for (let k = 1; k < seq.length; k++) {
    const oldC = seq[k - 1] ?? ' ';
    const newC = seq[k] ?? ' ';
    cell.textContent = '';
    const half = (cls: string, c: string): void => {
      const h = document.createElement('span');
      h.className = cls;
      const inner = document.createElement('i');
      inner.textContent = c === ' ' ? NBSP : c;
      h.appendChild(inner);
      cell.appendChild(h);
    };
    half('tf-half tf-t', newC);
    half('tf-half tf-b', oldC);
    half('tf-half tf-t tf-flap-a', oldC);
    half('tf-half tf-b tf-flap-b', newC);
    await sleep(340, tok);
    cell.textContent = newC === ' ' ? NBSP : newC;
  }
}

interface BoardCtx {
  board: HTMLDivElement;
  rail: HTMLDivElement;
  badge: HTMLDivElement;
  typed: string;
  suggested: string;
  tok: CancelToken;
}

interface Layout {
  cw: number;
  gap: number;
  chH: number;
  x: (slot: number) => number;
  off: (s: string) => number;
  mk: (char: string, slot: number, o: number) => HTMLSpanElement;
}

// Layout del riel: cards de ancho fijo posicionados en slots absolutos. Cada
// string queda CENTRADO en el riel — al insertar, ambas mitades se apartan
// del medio; al borrar, ambas cierran (no solo la cola).
function layoutRail(ctx: BoardCtx, n: number): Layout {
  const boardW = ctx.board.clientWidth - 60; // descuenta badge + gaps
  // Ancho de celda = la letra más ancha de las que participan (typo + sugerido),
  // en la tipografía del partner. Ver measureCellWidth.
  const chars = ctx.typed + ctx.suggested;
  let fs = 22;
  let cw = Math.ceil(measureCellWidth(fs, chars)) + 8;
  let gap = 3;
  if (n * (cw + gap) > boardW && boardW > 0) {
    fs = Math.max(12, Math.floor((fs * boardW) / (n * (cw + gap))));
    cw = Math.ceil(measureCellWidth(fs, chars)) + 6;
    gap = 2;
  }
  const chH = Math.round(fs * 1.5);
  const x = (slot: number): number => slot * (cw + gap);
  const off = (s: string): number => ((n - s.length) * (cw + gap)) / 2;
  ctx.rail.innerHTML = '';
  ctx.rail.style.cssText = `width:${x(n - 1) + cw}px;height:${chH}px;font-size:${fs}px;font-family:${brandFont()}`;
  const mk = (char: string, slot: number, o: number): HTMLSpanElement => {
    const el = document.createElement('span');
    el.className = 'tf-cell';
    el.style.cssText = `left:${x(slot) + o}px;width:${cw}px;height:${chH}px`;
    el.textContent = char === ' ' ? NBSP : char;
    ctx.rail.appendChild(el);
    return el;
  };
  return { cw, gap, chH, x, off, mk };
}

// Estado estático para prefers-reduced-motion: dominio corregido con las
// letras cambiadas en accent, flecha de inserción visible y ✓.
function buildStatic(ctx: BoardCtx): void {
  const ops = domainEditScript(ctx.typed, ctx.suggested);
  const n = Math.max(ctx.typed.length, ctx.suggested.length);
  const { cw, x, off, mk } = layoutRail(ctx, n);
  const o = off(ctx.suggested);
  const untouched = new Set(
    ops.filter((op): op is EditOp & { type: 'keep' } => op.type === 'keep').map((op) => op.bi),
  );
  for (let i = 0; i < ctx.suggested.length; i++) {
    const el = mk(ctx.suggested[i] ?? ' ', i, o);
    if (!untouched.has(i)) el.classList.add('tf-good');
  }
  for (const op of ops) {
    if (op.type !== 'ins') continue;
    const m = document.createElement('span');
    m.className = 'tf-marker tf-m-good tf-show';
    m.style.left = `${x(op.bi) + o + cw / 2 - 7}px`;
    ctx.rail.appendChild(m);
  }
  ctx.badge.classList.add('tf-show', 'tf-ok');
}

// Una pasada: fwd = typo → corregido, rev = corregido → typo (rebobinado del
// loop). La corrección es UNA sola operación: el gesto, el viraje de color y
// el ✕→✓ ocurren en simultáneo.
async function pass(ctx: BoardCtx, dir: 'fwd' | 'rev', cold: boolean): Promise<void> {
  const { rail, badge, tok } = ctx;
  const src = dir === 'fwd' ? ctx.typed : ctx.suggested;
  const dst = dir === 'fwd' ? ctx.suggested : ctx.typed;
  const ops = domainEditScript(src, dst);
  const n = Math.max(src.length, dst.length);
  const { cw, gap, x, off, mk } = layoutRail(ctx, n);
  const offA = off(src);
  const offB = off(dst);

  interface Cell {
    op: EditOp;
    el: HTMLSpanElement | null;
    el2?: HTMLSpanElement;
  }
  const cells: Cell[] = [];
  for (const op of ops) {
    switch (op.type) {
      case 'ins':
        cells.push({ op, el: null });
        break;
      case 'tr':
        cells.push({ op, el: mk(src[op.ai] ?? ' ', op.ai, offA), el2: mk(src[op.ai + 1] ?? ' ', op.ai + 1, offA) });
        break;
      default:
        cells.push({ op, el: mk(src[op.ai] ?? ' ', op.ai, offA) });
    }
  }
  const changedEls: HTMLSpanElement[] = [];
  for (const c of cells) {
    if (c.op.type === 'keep') continue;
    if (c.el) changedEls.push(c.el);
    if (c.el2) changedEls.push(c.el2);
  }

  const leave = dir === 'fwd' ? 'tf-bad' : 'tf-good';
  const arrive = dir === 'fwd' ? 'tf-good' : 'tf-bad';

  // Flechas de inserción: nacen en el seam del layout inicial y viajan al
  // centro de la celda insertada. Nunca se ocultan: en el reposo de error la
  // flecha queda siempre visible señalando dónde falta la letra.
  const markers: { el: HTMLSpanElement; tx: number }[] = [];
  ops.forEach((op, k) => {
    if (op.type !== 'ins') return;
    const nxt = ops.slice(k + 1).find((o): o is Exclude<EditOp, { type: 'ins' }> => o.type !== 'ins');
    const m = document.createElement('span');
    m.className = `tf-marker ${dir === 'fwd' ? 'tf-m-bad' : 'tf-m-good'}`;
    m.style.left = `${x(nxt ? nxt.ai : src.length) + offA - gap / 2 - 7}px`;
    rail.appendChild(m);
    markers.push({ el: m, tx: x(op.bi) + offB + cw / 2 - 7 });
  });
  const showMarkers = (): void => markers.forEach((m) => m.el.classList.add('tf-show'));

  if (dir === 'fwd') {
    if (cold) {
      // entrada fría: aparece neutro, recién después se marca el error
      await sleep(270, tok);
    }
    changedEls.forEach((el) => el.classList.add('tf-bad'));
    showMarkers();
    badge.classList.remove('tf-ok');
    badge.classList.add('tf-show', 'tf-err');
    // el reposo en rojo vive acá (tablero ya reconstruido, flecha visible)
    await sleep(cold ? 570 : 900, tok);
  } else {
    changedEls.forEach((el) => el.classList.add('tf-good'));
    showMarkers();
    badge.classList.remove('tf-err');
    badge.classList.add('tf-show', 'tf-ok');
    await sleep(180, tok);
  }

  // Los borrados van justo antes del resto: la letra que sobra se va con su
  // color de origen (virar mientras desaparece daría mensaje contradictorio).
  const dels = cells.filter((c) => c.op.type === 'del');
  if (dels.length > 0) {
    await Promise.all(
      dels.map((c) => (c.el ? flapTo(c.el, [c.el.textContent ?? ' ', ' '], tok) : Promise.resolve())),
    );
    dels.forEach((c) => c.el?.classList.add('tf-gone'));
    await sleep(240, tok);
  }

  // ✕→✓ y colores viran JUNTO con el gesto, no como paso posterior.
  badge.classList.remove(dir === 'fwd' ? 'tf-err' : 'tf-ok');
  badge.classList.add(dir === 'fwd' ? 'tf-ok' : 'tf-err');

  let moved = false;
  let insIdx = 0;
  const flaps: Promise<void>[] = [];
  for (const c of cells) {
    const op = c.op;
    if (op.type === 'tr' && c.el && c.el2) {
      for (const el of [c.el, c.el2]) {
        el.classList.remove(leave);
        el.classList.add(arrive);
      }
      c.el.classList.add('tf-over');
      c.el.style.left = `${x(op.bi + 1) + offB}px`;
      c.el2.classList.add('tf-under');
      c.el2.style.left = `${x(op.bi) + offB}px`;
      moved = true;
    } else if (op.type === 'sub' && c.el) {
      const el = c.el;
      const tx = x(op.bi) + offB;
      if (tx !== x(op.ai) + offA) {
        el.style.left = `${tx}px`;
        moved = true;
      }
      const oldC = src[op.ai] ?? ' ';
      const newC = dst[op.bi] ?? ' ';
      flaps.push(
        // la letra vira de color cuando el flap ATERRIZA (misma operación,
        // pero nunca se ve la letra vieja pintada del color "corregido")
        flapTo(el, [oldC, wobble(oldC, newC), newC], tok).then(() => {
          el.classList.remove(leave);
          el.classList.add(arrive);
        }),
      );
    } else if (op.type === 'ins') {
      const el = mk(' ', op.bi, offB);
      el.classList.add(arrive);
      c.el = el;
      const mrk = markers[insIdx++];
      if (mrk) mrk.el.style.left = `${mrk.tx}px`; // viaja con la apertura del hueco
      flaps.push(
        flapTo(el, [' ', dst[op.bi] ?? ' '], tok).then(() => {
          if (!mrk) return;
          mrk.el.classList.remove(dir === 'fwd' ? 'tf-m-bad' : 'tf-m-good');
          mrk.el.classList.add(dir === 'fwd' ? 'tf-m-good' : 'tf-m-bad');
        }),
      );
    } else if (op.type === 'keep' && c.el) {
      const tx = x(op.bi) + offB;
      if (tx !== x(op.ai) + offA) {
        c.el.style.left = `${tx}px`;
        moved = true;
      }
    }
  }
  const waits: Promise<unknown>[] = [];
  if (moved) waits.push(sleep(680, tok));
  if (flaps.length > 0) waits.push(Promise.all(flaps));
  if (waits.length > 0) await Promise.all(waits);
}

// Loop continuo: typo → corregido (pausa en ✓) → rebobinado → typo (pausa en
// ✕, con la flecha de inserción visible) → …  El reposo en rojo ocurre dentro
// de la pasada fwd, con el tablero ya reconstruido — así la flecha nunca
// desaparece durante el estado fijo de error.
async function runLoop(ctx: BoardCtx): Promise<void> {
  try {
    await pass(ctx, 'fwd', true);
    for (;;) {
      await sleep(840, ctx.tok); // reposo en corregido ✓
      await pass(ctx, 'rev', false);
      await pass(ctx, 'fwd', false); // incluye el reposo en typo ✕
    }
  } catch (e) {
    if (e !== CANCELLED) throw e;
  }
}

export function TypoBoard({ typed, suggested }: { typed: string; suggested: string }): JSX.Element {
  const boardRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const board = boardRef.current;
    const rail = railRef.current;
    const badge = badgeRef.current;
    if (!board || !rail || !badge) return undefined;
    const tok: CancelToken = { cancelled: false };
    const ctx: BoardCtx = { board, rail, badge, typed, suggested, tok };
    void (async () => {
      // El ancho de celda se calcula midiendo la fuente del partner: si
      // midiéramos antes de que termine de bajar, el tablero quedaría calibrado
      // para la fuente de fallback y las letras bailarían dentro de las celdas.
      // `document.fonts` no existe en navegadores viejos → seguimos igual.
      try {
        await document.fonts?.ready;
      } catch {
        /* medimos con lo que haya */
      }
      if (tok.cancelled) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        buildStatic(ctx);
      } else {
        void runLoop(ctx);
      }
    })();
    return () => {
      tok.cancelled = true;
      rail.innerHTML = '';
      badge.className = 'tf-badge';
    };
  }, [typed, suggested]);

  // Decorativo: el texto del banner ya comunica la sugerencia (a11y).
  return (
    <div ref={boardRef} className="tf-board" aria-hidden="true">
      <div ref={railRef} className="tf-rail" />
      <div ref={badgeRef} className="tf-badge">
        <svg
          className="tf-ic-x"
          width="24"
          height="24"
          viewBox="0 0 16 16"
          fill="none"
          strokeWidth="2.4"
          strokeLinecap="round"
        >
          <path d="M3.5 3.5 L12.5 12.5 M12.5 3.5 L3.5 12.5" />
        </svg>
        <svg
          className="tf-ic-ok"
          width="28"
          height="28"
          viewBox="0 0 18 18"
          fill="none"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 9.5 L7 13.5 L15 4.5" />
        </svg>
      </div>
    </div>
  );
}

export default function EmailTypoSuggestion({
  suggestion,
  onAccept,
  onKeep,
}: {
  suggestion: DomainSuggestion;
  onAccept: () => void;
  onKeep: () => void;
}): JSX.Element {
  const { typedDomain, suggestedDomain, fixedEmail } = suggestion;
  const localPart = fixedEmail.slice(0, fixedEmail.lastIndexOf('@'));
  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex flex-col gap-3 rounded-lg border border-accent/30 bg-accent/10 p-3 text-sm text-text"
    >
      <p>
        Tal vez quisiste poner <strong>{suggestedDomain}</strong>
      </p>
      <TypoBoard typed={typedDomain} suggested={suggestedDomain} />
      <div className="flex flex-wrap items-center gap-3">
        {/* Mismo vocabulario que el resto de los botones, en su talle chico:
            dentro de un banner inline un boton de 44px pesaria mas que el
            texto, pero el radio y la tipografia son los de siempre. */}
        <button type="button" onClick={onAccept} className={btn('accent', 'sm')}>
          {/* Un solo hijo: el `gap` del botón separa ícono de texto, y sin este
              span abriría un hueco entre la arroba y el dominio. */}
          <span>
            Sí, usar {localPart}@<strong className="font-semibold">{suggestedDomain}</strong>
          </span>
        </button>
        <button
          type="button"
          onClick={onKeep}
          className="text-sm text-text-muted underline underline-offset-2 hover:text-text"
        >
          Dejar como está
        </button>
      </div>
    </div>
  );
}
